import React from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { TextInput } from 'src/components/Forms/FormInputs/TextInput';
import { Select } from 'src/components/Forms/FormInputs/Select';
import { FileInput } from 'src/components/Forms/FormInputs/FileInput';
import { Button } from 'src/components/Button';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { api } from 'src/utils/api';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { getToken } from 'src/utils/localStorage';

const problemTypes = [
  { label: 'Bug', key: 'bug' },
  { label: 'Support Request', key: 'support' },
  { label: 'Feature Request', key: 'suggestion' },
  { label: 'Other', key: 'other' },
];

interface SupportFormValues {
  name: string;
  email: string;
  description: string;
  problemType: string;
  attachment: File | null;
}

const defaultValues: SupportFormValues = {
  name: '',
  email: '',
  description: '',
  problemType: '',
  attachment: null,
};

const Support: React.FC = () => {
  const { user } = useAccount();
  const showSnackbar = useSnackbar();
  const methods = useForm<SupportFormValues>({
    defaultValues: { ...defaultValues, email: user?.email || '' },
    mode: 'onChange',
  });

  // Aggiorna l'email quando l'utente cambia
  React.useEffect(() => {
    if (user?.email) {
      methods.setValue('email', user.email);
    }
  }, [user?.email, methods]);

  const onSubmit: SubmitHandler<SupportFormValues> = async (data) => {
    try {
      // Verify that user is authenticated
      if (!user || !user.id) {
        showSnackbar(
          'You must be authenticated to send a support request.',
          'error',
        );
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('description', data.description);
      formData.append('problemType', data.problemType);
      if (data.attachment) {
        formData.append('attachment', data.attachment);
      }

      // Ottieni il token JWT usando la funzione standard del sistema
      const token = getToken();

      if (!token) {
        showSnackbar(
          'Authentication token missing. Please log in again.',
          'error',
        );
        return;
      }

      await api.post('/support', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      showSnackbar(
        'Request sent! Thanks for contacting us, we will get back to you as soon as possible.',
        'success',
      );

      // Reset del form dopo il successo
      methods.reset(defaultValues);
    } catch (e: any) {
      if (e.response) {
        if (e.response.status === 401) {
          showSnackbar(
            'Sessione scaduta. Effettua nuovamente il login.',
            'error',
          );
        } else if (e.response.data && e.response.data.error) {
          showSnackbar(
            'Error sending support request: ' + e.response.data.error,
            'error',
          );
        } else {
          showSnackbar('Error sending support request.', 'error');
        }
      } else {
        showSnackbar('Error sending support request.', 'error');
      }

      if (process.env.NODE_ENV !== 'production') {
        // TODO: gestire logging in modo appropriato in produzione
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        py: 2,
        pt: { xs: '72px', sm: 2 }, // aggiunge spazio sotto l'header solo su mobile
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100vw',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          pl: { xs: 0, md: '240px' },
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, sm: 3 }, // padding orizzontale mobile/tablet
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 3,
            minHeight: 650,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflowY: 'auto',
            pb: 3, // padding inferiore aggiunto
          }}
        >
          <Typography
            variant="h4"
            mt={2.5}
            mb={2}
            fontWeight={600}
            color="primary.main"
          >
            Support
          </Typography>
          <Typography variant="body1" mb={4} color="text.secondary">
            Fill out the form to report a bug, send a support request, or
            suggest an improvement. We will respond as soon as possible.
          </Typography>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextInput
                  name="name"
                  label="Name"
                  fullWidth
                  margin="none"
                  required
                />
                <TextInput
                  name="email"
                  label="Email"
                  fullWidth
                  margin="none"
                  required
                  disabled
                />
                <Select
                  name="problemType"
                  options={problemTypes}
                  placeholder="Problem type"
                />
                <TextInput
                  name="description"
                  label="Description"
                  fullWidth
                  margin="none"
                  required
                  multiline
                  minRows={2}
                />
                <FileInput
                  name="attachment"
                  label="Attach screenshot (optional)"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    py: 1.3,
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  Send request
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default Support;

// Finish!
